import type { InboxMessageFragment } from "@qw/graphql";
import { Button, DropdownMenuItem, Icon, MoreMenu, Spinner, Tooltip, TooltipContent, TooltipTrigger } from "@qw/ui";
import type { FC } from "react";

import { useConversation } from "../providers/use-conversation";
import { useCreateForwardDraft } from "../providers/use-create-forward-draft";
import { useCreateReplyDraft } from "../providers/use-create-reply-draft";
import { useDraftsCompose } from "../providers/use-drafts-compose";

export interface MessageActionsProps {
  message: InboxMessageFragment;
}

export const MessageActions: FC<MessageActionsProps> = ({ message }) => {
  const [createReplyDraft, { loading: isReplyDraftLoading }] = useCreateReplyDraft();
  const [createForwardDraft, { loading: isForwardDraftLoading }] = useCreateForwardDraft();
  const { inboxConversation } = useConversation();
  const { getDraftPlacementForConversation } = useDraftsCompose();

  // Stitched collaborator threads union messages from sibling conversation copies, so
  // `message.conversationId` can name another mailbox's copy rather than the thread on screen.
  // Source the draft to the conversation the user actually has open, otherwise the new draft keys
  // under a conversation the inline area never looks up and stays hidden until the thread-drafts
  // refetch reassigns it.
  const sourceConversationId = inboxConversation?.id;
  const draftOptions = {
    sourceConversationId,
    placement: getDraftPlacementForConversation(sourceConversationId),
  };
  const handleReplyClick = () => createReplyDraft({ messageId: message.id }, draftOptions);
  const handleReplyAllClick = () => createReplyDraft({ messageId: message.id, replyAll: true }, draftOptions);
  const handleForwardClick = () => createForwardDraft({ messageId: message.id }, draftOptions);

  return (
    <div className="flex items-center flex-nowrap flex-0 gap-1 mt-3.5">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="xs" display="icon" onClick={handleReplyClick} disabled={isReplyDraftLoading}>
            {isReplyDraftLoading ? <Spinner /> : <Icon icon="reply" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>Reply</TooltipContent>
      </Tooltip>

      <MoreMenu isLoading={isForwardDraftLoading}>
        <DropdownMenuItem onSelect={handleReplyClick} disabled={isReplyDraftLoading}>
          <Icon icon="reply" /> Reply
        </DropdownMenuItem>

        {message.canReplyAll && (
          <DropdownMenuItem onSelect={handleReplyAllClick} disabled={isReplyDraftLoading}>
            <Icon icon="reply_all" /> Reply All
          </DropdownMenuItem>
        )}

        <DropdownMenuItem onSelect={handleForwardClick} disabled={isForwardDraftLoading}>
          <Icon icon="forward" /> Forward
        </DropdownMenuItem>
      </MoreMenu>
    </div>
  );
};
